import { api } from "./client";
import type {
  OrganizationAccessWindow,
  Organization,
  OrganizationMember,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  AddMemberInput,
  OrgRole,
} from "../types/organization";

interface ListOrganizationsResponse {
  organizations: Organization[];
}

interface ListMembersResponse {
  members: OrganizationMember[];
}

interface GetMyRoleResponse {
  role: OrgRole;
  isDemo?: boolean;
  maxSeats?: number | null;
  maxSops?: number | null;
  maxUploadBytes?: number | null;
  features?: Record<string, boolean>;
  windows?: OrganizationAccessWindow[];
  remainingSops?: number | null;
  demoExpiresAt?: string | null;
  demoAcceptedAt?: string | null;
}

interface CreateOrganizationResponse {
  orgId: string;
  name: string;
  slug: string;
  frontendBaseUrl?: string;
  createdAt: string;
}

interface UpdateOrganizationResponse {
  orgId: string;
  name: string;
  slug: string;
  frontendBaseUrl?: string;
  maxSeats?: number | null;
  maxSops?: number | null;
  maxUploadBytes?: number | null;
  createdAt: string;
}

interface AddMemberResponse {
  orgId: string;
  userId: string;
  role: OrgRole;
}

interface UpdateMemberRoleResponse {
  orgId: string;
  userId: string;
  role: OrgRole;
}

export const organizationService = {
  listOrganizations: async (): Promise<Organization[]> => {
    const response = await api.get<ListOrganizationsResponse>("/organizations");
    return response.data.organizations ?? [];
  },

  getMyRole: async (orgId: string): Promise<OrgRole | null> => {
    const response = await api.get<GetMyRoleResponse>(
      `/organizations/${orgId}/me`,
    );
    return response.data?.role ?? null;
  },

  getMyOrganizationContext: async (orgId: string): Promise<GetMyRoleResponse> => {
    const response = await api.get<GetMyRoleResponse>(
      `/organizations/${orgId}/me`,
    );
    return response.data;
  },

  listMembers: async (orgId: string): Promise<OrganizationMember[]> => {
    const response = await api.get<ListMembersResponse>(
      `/organizations/${orgId}/members`,
    );
    return response.data.members ?? [];
  },

  addMember: async (
    orgId: string,
    input: AddMemberInput,
  ): Promise<AddMemberResponse> => {
    const response = await api.post<AddMemberResponse>(
      `/organizations/${orgId}/members`,
      input,
    );
    return response.data;
  },

  removeMember: async (
    orgId: string,
    userId: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(
      `/organizations/${orgId}/members/${userId}`,
    );
    return response.data;
  },

  updateMemberRole: async (
    orgId: string,
    userId: string,
    role: OrgRole,
  ): Promise<UpdateMemberRoleResponse> => {
    const response = await api.put<UpdateMemberRoleResponse>(
      `/organizations/${orgId}/members/${userId}/role`,
      { role },
    );
    return response.data;
  },

  createOrganization: async (
    input: CreateOrganizationInput,
  ): Promise<CreateOrganizationResponse> => {
    const response = await api.post<CreateOrganizationResponse>(
      "/organizations",
      input,
    );
    return response.data;
  },

  updateOrganization: async (
    orgId: string,
    input: UpdateOrganizationInput,
  ): Promise<UpdateOrganizationResponse> => {
    const response = await api.patch<UpdateOrganizationResponse>(
      `/organizations/${orgId}`,
      input,
    );
    return response.data;
  },

  deleteOrganization: async (orgId: string): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(
      `/organizations/${orgId}`,
    );
    return response.data;
  },
};
